<script setup lang="ts">
import { computed, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n";

import { Day } from "@/Common/Models/Domain/Day";
import type { CalendarEvent } from "@/Modules/Calendars/Models/Domain";

import EventCard from "../EventCard.vue";
import EmptyList from "./components/EmptyList.vue";

import {
  getDateLabelFromUnixSeconds,
  localeDateFormatFromDateTime,
  unixSecondsToDateTime,
} from "@/Utils/Date";
import { hasValue } from "@/Utils/Objects";

export interface EventListProps {
  events: CalendarEvent[];
  timezone: string;
}

const props = defineProps<EventListProps>();
const { t } = useI18n();

const emit = defineEmits<{
  (event: "viewEvent", value: CalendarEvent): void;
}>();

const hasViewEvent = computed(() => {
  return hasValue(getCurrentInstance()?.vnode.props?.onViewEvent);
});

const eventsMap = computed((): Map<number, CalendarEvent[]> => {
  const map = new Map<number, CalendarEvent[]>();

  props.events.forEach((event) => {
    if (event.isDeleted) return;

    const date = unixSecondsToDateTime(event.startAt);
    const key = date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toUnixInteger();

    if (map.has(key)) {
      map.get(key)!.push(event);
    } else {
      map.set(key, [event]);
    }
  });

  return map;
});

const formattedDate = (date: number): string => timestampToDateString(date);

const timestampToDateString = (timestamp: number): string => {
  const dateTime = unixSecondsToDateTime(timestamp);
  const dateLabel = getDateLabelFromUnixSeconds(timestamp);
  const dateString = localeDateFormatFromDateTime(dateTime, false, false, false);

  if ([Day.TODAY, Day.TOMORROW].includes(dateLabel)) {
    return `${t(`general.chat.${dateLabel}`)}, ${dateString}`;
  }

  return dateString;
};

const handleViewEvent = (event: CalendarEvent) => {
  emit("viewEvent", event);
};
</script>

<template>
  <EmptyList v-if="props.events.length === 0" />

  <div class="event_list" v-else>
    <section v-for="date in eventsMap.keys()" class="event_list__section" :key="date" :id="date.toString()">
      <p class="event_list__section__date">{{ formattedDate(date) }}</p>

      <EventCard
        v-for="event in eventsMap.get(date)!"
        class="event_list__section__item"
        :class="{ example: hasViewEvent }"
        :key="event.uuid"
        :event="event"
        :timezone="props.timezone"
        @select="hasViewEvent ? handleViewEvent(event) : undefined"
      />
    </section>
  </div>
</template>

<style scoped lang="scss">
.event_list {
  &__section {
    @include sp.padding(b, 5);

    &__date {
      @include colors.color(content, high);
      @include sp.padding(b, 2);
      @include text.general(high, xs);

      &::first-letter {
        text-transform: uppercase;
      }
    }

    &__item {
      @include sp.margin(b, 4);

      &:last-child {
        margin-bottom: 0;
      }
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
